import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { EmailDiagnostics } from "@/components/admin/EmailHealth";

export default function SettingsTab() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <p className="text-muted-foreground">Loading…</p>;

  const set = (k, v) => setS({ ...s, [k]: v });
  const setBank = (k, v) => setS({ ...s, bank_details: { ...s.bank_details, [k]: v } });
  const setStore = (k, v) => setS({ ...s, store: { ...s.store, [k]: v } });

  const save = async () => {
    try {
      const payload = {
        markup_percentage: Number(s.markup_percentage), gst_rate: Number(s.gst_rate),
        gst_registered: s.gst_registered, free_shipping_threshold: Number(s.free_shipping_threshold),
        standard_shipping_fee: Number(s.standard_shipping_fee), shipping_country: s.shipping_country,
        online_sales_countries: s.online_sales_countries,
        bank_transfer_enabled: s.bank_transfer_enabled, stripe_enabled: s.stripe_enabled,
        customer_emails_enabled: s.customer_emails_enabled, admin_notifications_enabled: s.admin_notifications_enabled,
        admin_notification_email: s.admin_notification_email, supplier_default_gst_treatment: s.supplier_default_gst_treatment,
        dealer_report_emails_enabled: s.dealer_report_emails_enabled, scraper_report_emails_enabled: s.scraper_report_emails_enabled,
        enquiry_ack_enabled: s.enquiry_ack_enabled,
        reply_to_email: s.reply_to_email, public_base_url: s.public_base_url, elive_feed_url: s.elive_feed_url,
        price_anomaly_pct: Number(s.price_anomaly_pct), price_anomaly_abs: Number(s.price_anomaly_abs),
        bank_details: s.bank_details, store: s.store,
        customer_confirmation_message: s.customer_confirmation_message,
        free_shipping_public_message: s.free_shipping_public_message,
        footer_shipping_text: s.footer_shipping_text,
        homepage_categories_enabled: s.homepage_categories_enabled,
        homepage_brands_enabled: s.homepage_brands_enabled,
        homepage_brands_show_count: s.homepage_brands_show_count,
        homepage_product_block1_enabled: s.homepage_product_block1_enabled,
        homepage_product_block2_enabled: s.homepage_product_block2_enabled,
        homepage_product_block3_enabled: s.homepage_product_block3_enabled,
        help_text: s.help_text, returns_text: s.returns_text,
      };
      await api.put("/admin/settings", payload);
      toast.success("Commerce settings saved — prices recalculated where needed");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  return (
    <div className="max-w-3xl">
      <h1 className="text-3xl font-extrabold mb-1">Commerce Settings</h1>
      <p className="text-muted-foreground mb-6">All commercial rules are configuration-driven. Change them here without touching code.</p>

      <Section title="Pricing & GST">
        <Grid>
          <Num label="Default Supplier Markup (%)" testid="set-markup" value={s.markup_percentage} onChange={(v) => set("markup_percentage", v)} />
          <Num label="NZ GST Rate (%)" testid="set-gst" value={s.gst_rate} onChange={(v) => set("gst_rate", v)} />
        </Grid>
        <Toggle label="GST Registered (prices include GST)" checked={s.gst_registered} onChange={(v) => set("gst_registered", v)} testid="set-gst-registered" />
      </Section>

      <Section title="Shipping (New Zealand)">
        <Grid>
          <Num label="Free Shipping Threshold (NZD)" testid="set-threshold" value={s.free_shipping_threshold} onChange={(v) => set("free_shipping_threshold", v)} />
          <Num label="Standard Shipping Fee (NZD)" testid="set-shipping-fee" value={s.standard_shipping_fee} onChange={(v) => set("standard_shipping_fee", v)} />
        </Grid>
        <div><Label>Free Shipping Public Message</Label>
          <Input data-testid="set-shipping-msg" value={s.free_shipping_public_message || ""} onChange={(e) => set("free_shipping_public_message", e.target.value)} /></div>
        <div><Label>Footer Shipping Text</Label>
          <Textarea data-testid="set-footer-shipping" rows={4} value={s.footer_shipping_text || ""} onChange={(e) => set("footer_shipping_text", e.target.value)} />
          <p className="text-xs text-muted-foreground mt-1">Shown in the site footer's “Shipping” column. Line breaks are preserved.</p></div>
        <div><Label>Shipping Country</Label><Input data-testid="set-country" value={s.shipping_country} onChange={(e) => set("shipping_country", e.target.value)} /></div>
      </Section>

      <Section title="Payment Providers">
        <Toggle label="Bank Transfer — Enabled at launch" checked={s.bank_transfer_enabled} onChange={(v) => set("bank_transfer_enabled", v)} testid="set-bank-enabled" />
        <Toggle label="Stripe — Secure Online Payment (future)" checked={s.stripe_enabled} onChange={(v) => set("stripe_enabled", v)} testid="set-stripe-enabled" />
      </Section>

      <Section title="Bank Account Details">
        <Grid>
          <Txt label="Bank Name" testid="set-bank-name" value={s.bank_details?.bank_name} onChange={(v) => setBank("bank_name", v)} />
          <Txt label="Account Name" testid="set-account-name" value={s.bank_details?.account_name} onChange={(v) => setBank("account_name", v)} />
          <Txt label="Account Number" testid="set-account-number" value={s.bank_details?.account_number} onChange={(v) => setBank("account_number", v)} />
          <Txt label="Reference Label" testid="set-ref-label" value={s.bank_details?.particulars_label} onChange={(v) => setBank("particulars_label", v)} />
        </Grid>
      </Section>

      <Section title="Emails">
        <Toggle label="Send Customer Order Confirmation Emails" checked={s.customer_emails_enabled} onChange={(v) => set("customer_emails_enabled", v)} testid="set-customer-emails" />
        <Toggle label="Send Admin New-Order Notifications" checked={s.admin_notifications_enabled} onChange={(v) => set("admin_notifications_enabled", v)} testid="set-admin-notify" />
        <Toggle label="Send customers an enquiry acknowledgement email" checked={s.enquiry_ack_enabled !== false} onChange={(v) => set("enquiry_ack_enabled", v)} testid="set-enquiry-ack" />
        <Toggle label="Email daily Dealer FTP import report" checked={s.dealer_report_emails_enabled !== false} onChange={(v) => set("dealer_report_emails_enabled", v)} testid="set-dealer-report-emails" />
        <Toggle label="Email Supplier Scraper (cron) reports" checked={s.scraper_report_emails_enabled !== false} onChange={(v) => set("scraper_report_emails_enabled", v)} testid="set-scraper-report-emails" />
        <p className="text-xs text-muted-foreground -mt-2">Imports and scrapers keep running either way; reports stay in Supplier Import / Supplier Scrapers history.</p>
        <Txt label="Admin Notification Email" testid="set-admin-email" value={s.admin_notification_email} onChange={(v) => set("admin_notification_email", v)} />
        <Txt label="Customer Reply-To Email (monitored inbox)" testid="set-reply-to" value={s.reply_to_email} onChange={(v) => set("reply_to_email", v)} />
        <div><Label>Customer Confirmation Message</Label>
          <Textarea data-testid="set-confirm-msg" rows={3} value={s.customer_confirmation_message} onChange={(e) => set("customer_confirmation_message", e.target.value)} /></div>
        <EmailDiagnostics />
      </Section>

      <Section title="Help Center Content">
        <div><Label>Help Page Text</Label>
          <Textarea data-testid="set-help-text" rows={6} value={s.help_text || ""} onChange={(e) => set("help_text", e.target.value)} /></div>
        <div><Label>Returns Page Text</Label>
          <Textarea data-testid="set-returns-text" rows={6} value={s.returns_text || ""} onChange={(e) => set("returns_text", e.target.value)} /></div>
        <p className="text-xs text-muted-foreground">Line breaks are preserved on the public Help Center pages.</p>
      </Section>

      <Section title="Homepage">
        <Toggle label="Show category block below header on homepage" checked={s.homepage_categories_enabled !== false} onChange={(v) => set("homepage_categories_enabled", v)} testid="set-homepage-cats" />
        <Toggle label="Show brand block on homepage" checked={s.homepage_brands_enabled !== false} onChange={(v) => set("homepage_brands_enabled", v)} testid="set-homepage-brands" />
        <Toggle label="Show product count on brand block" checked={s.homepage_brands_show_count !== false} onChange={(v) => set("homepage_brands_show_count", v)} testid="set-homepage-brands-count" />
        <Toggle label="Show homepage product block 1 (above footer)" checked={s.homepage_product_block1_enabled !== false} onChange={(v) => set("homepage_product_block1_enabled", v)} testid="set-homepage-block1" />
        <Toggle label="Show homepage product block 2 (above footer)" checked={s.homepage_product_block2_enabled !== false} onChange={(v) => set("homepage_product_block2_enabled", v)} testid="set-homepage-block2" />
        <Toggle label="Show homepage product block 3 (above footer)" checked={s.homepage_product_block3_enabled !== false} onChange={(v) => set("homepage_product_block3_enabled", v)} testid="set-homepage-block3" />
        <p className="text-xs text-muted-foreground">Pick up to 40 categories and up to 50 brands under <strong>Categories &amp; Brands → Homepage</strong>. Curate the two product blocks (up to 20 each) under <strong>Homepage Blocks</strong>. All scroll horizontally with arrows on both sides.</p>
      </Section>

      <Section title="Price Safety Thresholds">
        <Grid>
          <Num label="Flag if cost changes more than (%)" testid="set-anomaly-pct" value={s.price_anomaly_pct} onChange={(v) => set("price_anomaly_pct", v)} />
          <Num label="Flag if cost changes more than (NZD)" testid="set-anomaly-abs" value={s.price_anomaly_abs} onChange={(v) => set("price_anomaly_abs", v)} />
        </Grid>
      </Section>

      <Section title="Public Site & Tax Invoice">
        <Txt label="Public Site URL (used for invoice links in emails)" testid="set-public-url" value={s.public_base_url} onChange={(v) => set("public_base_url", v)} />
        <Txt label="GST Number (shown on tax invoices)" testid="set-gst-number" value={s.store?.gst_number} onChange={(v) => setStore("gst_number", v)} />
      </Section>

      <Section title="Competitor Price Match (elive)">
        <Txt label="Competitor Feed URL (CSV) — synced daily, never shown to customers" testid="set-elive-url" value={s.elive_feed_url} onChange={(v) => set("elive_feed_url", v)} />
        <Button type="button" variant="outline" data-testid="sync-pricematch" onClick={async () => {
          try { const { data } = await api.post("/admin/price-match/sync"); toast.success(`Price sync complete — ${data.matched} products matched`); }
          catch { toast.error("Sync failed"); }
        }}>Sync Competitor Prices Now</Button>
      </Section>

      <Section title="Store Contact">
        <Grid>
          <Txt label="Company Name" testid="set-company" value={s.store?.company_name} onChange={(v) => setStore("company_name", v)} />
          <Txt label="Contact Email" testid="set-contact-email" value={s.store?.contact_email} onChange={(v) => setStore("contact_email", v)} />
          <Txt label="Contact Phone" testid="set-contact-phone" value={s.store?.contact_phone} onChange={(v) => setStore("contact_phone", v)} />
          <Txt label="Address" testid="set-address" value={s.store?.address} onChange={(v) => setStore("address", v)} />
        </Grid>
      </Section>

      <Button data-testid="save-settings" onClick={save} className="bg-accent text-steel hover:bg-amber-600 font-semibold">
        <Save size={16} className="mr-2" /> Save Settings
      </Button>
    </div>
  );
}

function Section({ title, children }) {
  return <div className="rounded-xl border bg-white p-6 mb-5 space-y-4"><h2 className="font-heading font-bold text-lg">{title}</h2>{children}</div>;
}
function Grid({ children }) { return <div className="grid sm:grid-cols-2 gap-4">{children}</div>; }
function Num({ label, value, onChange, testid }) {
  return <div><Label>{label}</Label><Input data-testid={testid} type="number" value={value} onChange={(e) => onChange(e.target.value)} /></div>;
}
function Txt({ label, value, onChange, testid }) {
  return <div><Label>{label}</Label><Input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} /></div>;
}
function Toggle({ label, checked, onChange, testid }) {
  return <label className="flex items-center gap-3 text-sm"><Switch data-testid={testid} checked={checked} onCheckedChange={onChange} /> {label}</label>;
}
